// @ts-ignore
import $i18n from '@/i18n';
import React, { useEffect, useMemo, useRef } from 'react';
import { useState } from 'react'
import { Table, Message, Button, Input, Icon } from '@b-design/fusion';
import PaginationComponent from '@/components/PaginationComponent';
import style from './style.module.less'
import './index.less'
import { PlusOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { tableColumons } from './config'
import { getJsApiAuthList, addJsapiToAuthList, removeJsapiToAuthList, addJsApi } from '@/services/jsapiAuth'
import lodash from 'lodash'

import { useRowSelection } from './useRowSelection'
import { useDialog, useAddCustomerJsApiDialog } from './useDialog';



interface IJsAPIAuthTableProps {
  openFlag: boolean;
  contentHeight?: number;
  callerData: any[];
  spaceId: string;
  channelCode: string
  createChannelId: string,
  isEdit: boolean
}
interface IRecord {
  id: string;
  jsapiName: string;
  functionDesc: string;
  channelName: string;
  supportSystem: string[];
  controlStatus: "CONTROLLED" | "UNCONTROLLED"

}

interface IDataSource {
  list: IRecord[],
  total: number
}


const JsAPIAuthTable = (props: IJsAPIAuthTableProps): JSX.Element | undefined => {
  const { openFlag, contentHeight = 560, callerData, spaceId, channelCode, createChannelId, isEdit } = props;
  const [dataSource, setDataSource] = useState<IDataSource>({ list: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState('')



  const addTempSetRef = useRef<Set<string>>(new Set())
  const removeTempSetRef = useRef<Set<string>>(new Set())


  const column = useMemo(() => {
    return tableColumons(addTempSetRef.current, removeTempSetRef.current)
  }, [addTempSetRef.current.size, removeTempSetRef.current.size])
  // 分页参数
  const [paginationValue, setPaginationValue] = useState<
    Record<'currentPage' | 'pageSize', number>
  >({
    currentPage: 1,
    pageSize: 10,
  });




  const {
    rowSelection,
    selectedSize, //选中的数量
    allSelectedRecords, // 获取已选中的记录
    allSelectedIds, // 获取已选中的记录ID
    clearRecord
  } = useRowSelection<IRecord>(dataSource, (record) => (`jsapiName`))

  const groupRecords = useMemo((): { in: IRecord[], notIn: IRecord[] } => {
    const group = lodash.groupBy(allSelectedRecords, (item: any) => item.controlStatus)
    return {
      in: group['CONTROLLED'] || [],
      notIn: group['UNCONTROLLED'] || []
    }
  }, [allSelectedRecords, JSON.stringify(allSelectedIds)])


  const { openDialog } = useDialog()


  const { openJsApiCustomerDialog } = useAddCustomerJsApiDialog()


  const fetchData = (pageSize: number, pageNumber: number, jsapiName?: string) => {
    setLoading(true)
    getJsApiAuthList({
      jsapiName: jsapiName ?? '',
      channelCode: isEdit ? channelCode : createChannelId,
      spaceId,
      currentPage: pageNumber,
      pageSize
    }).then((res) => {
      if (res) {
        const newList = res.list.map(item => {
          let controlStatus
          if (addTempSetRef.current.has(item.jsapiName)) {
            controlStatus = 'CONTROLLED'
          } else if (removeTempSetRef.current.has(item.jsapiName)) {
            controlStatus = 'UNCONTROLLED'
          } else {
            controlStatus = item.controlStatus
          }
          return {
            ...item,
            controlStatus
          }
        })
        setDataSource({
          list: newList,
          total: res.total
        })
        setPaginationValue({
          currentPage: res.current,
          pageSize: res.pageSize
        })
      } else {
        Message.error(res?.message)
      }
    }).finally(() => {
      setLoading(false)
    })
  }

  const refresh = (pageSize: number, pageNumber: number) => {
    fetchData(pageSize, pageNumber, inputValue)
  }


  useEffect(() => {
    if (openFlag) {
      fetchData(paginationValue.pageSize, paginationValue.currentPage, inputValue)
    }
  }, [paginationValue.currentPage, paginationValue.pageSize, openFlag])



  //将指定jsapi加入授权列表
  const addToAuthList = () => {
    openDialog({
      title: $i18n.get({ id: 'aopf.pages.channel.jsApiAuth.jsapiTable.btn.add', dm: '加入授权列表' }),
      content: $i18n.get({ id: 'aopf.pages.channel.jsApiAuth.jsapiTable.dialog.add.content', dm: '是否确认将选中信息加入授权列表?' }),
      onOk: () => {
        const jsapiList = groupRecords.notIn.map(record => {
          if (removeTempSetRef.current.has(record.jsapiName)) {
            removeTempSetRef.current.delete(record.jsapiName)
          }
          addTempSetRef.current.add(record.jsapiName)
          return record.jsapiName
        })
        clearRecord()
        refresh(paginationValue.pageSize, 1)
        sessionStorage.setItem('addTempSet', JSON.stringify(Array.from(addTempSetRef.current)))
        return Promise.resolve()

      },
      type: 'add'
    })
  }


  //录入自定义jsapi
  const registerJsApi = () => {
    const osDataSource = (callerData || []).map(item => {
      return {
        ...item,
        label: item.typeDesc,
        value: item.type
      }
    })
    openJsApiCustomerDialog({
      title: $i18n.get({ id: 'aopf.pages.channel.jsApiAuth.jsapiTable.btn.register', dm: '自定义 JSAPI 录入' }),
      osDataSource,
      onOk: (field) => {
        return addJsApi({
          ...field?.getValues(),
          spaceId,
          channelCode: isEdit ? channelCode : createChannelId,
        }).then(res => {
          if (res) {
            Message.success({
              title: $i18n.get({
                id: 'aopf.pages.channel.jsApiAuth.jsapiTable.message.success.registerJsapi',
                dm: '录入 JSAPI 成功',
              }),
            });
            refresh(paginationValue.pageSize, 1)
          } else {
            return Promise.reject(res)
          }
        })
      }
    })
  }

  //从授权列表中移除
  const removeFromAuthList = () => {
    openDialog({
      title: $i18n.get({ id: 'aopf.pages.channel.jsApiAuth.jsapiTable.btn.remove', dm: '从授权列表中移除' }),
      content: $i18n.get({ id: 'aopf.pages.channel.jsApiAuth.jsapiTable.dialog.remove.content', dm: '是否确认删除选中信息，删除后将从授权列表中移除' }),
      onOk: () => {
        const jsapiList = groupRecords.in.map(record => {
          if (addTempSetRef.current.has(record.jsapiName)) {
            addTempSetRef.current.delete(record.jsapiName)
          }
          removeTempSetRef.current.add(record.jsapiName)
          return record.jsapiName
        })
        clearRecord()
        refresh(paginationValue.pageSize, 1)
        sessionStorage.setItem('removeTempSet', JSON.stringify(Array.from(removeTempSetRef.current)))
        return Promise.resolve()
      },
      type: 'remove'
    })
  }




  const onSerch = (v) => {
    fetchData(paginationValue.pageSize, 1, inputValue)
  }

  const onKeyDown = (e) => {
    if (e.code === 'Enter') {
      onSerch(inputValue)
    }
  }

  const onChange = (v) => {
    setInputValue(v)
  }

  const search = () => {
    return (
      <div onKeyDown={onKeyDown} style={{ flex: 1 }}>
        <Input
          className="jsapi-searchInput"
          value={inputValue}
          onChange={onChange}
          placeholder={$i18n.get({ id: 'aopf.pages.miniapp.input.Search', dm: '搜索...' })}
          innerBefore={
            <Icon type="search" style={{ width: 12, height: 12, marginRight: '4' }} />
          }
        /></div>)
  }




  return openFlag ? (<div style={{ width: '100%' }}>
    <div style={{ marginBottom: '100px' }}>
      <div style={{ display: 'flex', flexFlow: 'row', justifyContent: 'space-between', marginBottom: 16, marginTop: 24, alignItems: 'center' }}>
        <div style={{ display: 'flex', flexFlow: 'row' }}>
          <Button
            disabled={selectedSize === 0 || groupRecords.notIn.length === 0}
            className={style.btn_base}
            style={{ color: 'var(--superapp-primary-1)', opacity: selectedSize === 0 || groupRecords.notIn.length === 0 ? 0.4 : 1, height: 24 }}
            type="secondary"
            onClick={() => {
              addToAuthList()
            }}
          >
            <PlusOutlined style={{ fontSize: 10, marginBottom: 2, marginRight: 4 }} /> {$i18n.get({ id: 'aopf.pages.channel.jsApiAuth.jsapiTable.btn.add', dm: '加入授权列表' })}
          </Button>
          <Button
            disabled={selectedSize === 0 || groupRecords.in.length === 0}
            className={style.btn_base}
            style={{ marginLeft: 8, color: 'var(--superapp-primary-1)', opacity: selectedSize === 0 || groupRecords.in.length === 0 ? 0.4 : 1 }}
            type="secondary"
            onClick={() => {
              removeFromAuthList()
            }}
          >

            <DeleteOutlined style={{ fontSize: 10, marginRight: 4 }} />{$i18n.get({ id: 'aopf.pages.channel.jsApiAuth.jsapiTable.btn.remove', dm: '从授权列表中移除' })}
          </Button>


        </div>

        {search()}

      </div>
      <div>
        <Button
          className={style.btn_base}
          style={{ marginBottom: 16 }}
          type="primary"
          onClick={() => {
            registerJsApi()
          }}
        >

          <SettingOutlined style={{ fontSize: 10, marginRight: 4 }} />{$i18n.get({ id: 'aopf.pages.channel.jsApiAuth.jsapiTable.btn.register', dm: '自定义 JSAPI 录入' })}
        </Button>
      </div>
      <div className="jsapi-auth-table-content" style={{ position: 'relative', padding: 0 }}>

        <Table.StickyLock
          className="jsapi-auth-table"
          primaryKey="jsapiName"
          columns={column} dataSource={dataSource?.list}
          rowSelection={rowSelection}
          fixedHeader
          hasBorder={false}
          maxBodyHeight={contentHeight - 48}
          loading={loading}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div className={style.toatlText} style={{ height: 32, display: 'flex', flexFlow: 'row', justifyContent: 'center', alignItems: 'center' }}>{`${selectedSize} / ${dataSource.total}`}</div>
          <PaginationComponent
            total={dataSource?.total}
            current={paginationValue?.currentPage}
            pageSize={paginationValue?.pageSize}
            onPageChange={(current, pageSize) => {
              setPaginationValue({
                currentPage: current,
                pageSize,
              });
            }}
          />
        </div>
      </div>

    </div>

  </div>) : undefined

}

export default JsAPIAuthTable;